import { useEffect, useRef, type KeyboardEvent } from 'react'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import { REVIEWER_HINT_LIMITS, TRADER_SIDES } from '@klear/constants/document-processing'
import { Button } from '@klear/ui/components/button'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Textarea } from '@klear/ui/components/textarea'
import { ChoiceGroup } from '@/app/document-review/choice-group'
import { withTrader, type PremiseDraft } from '@/app/document-review/premise'

export type ProcessAgainEditorProps = {
  draft: PremiseDraft
  keptCount: number
  traders: readonly { id: number; name: string | null }[]
  isPending: boolean
  onChange: (draft: PremiseDraft) => void
  onSubmit: () => void
  onCancel: () => void
}

export function ProcessAgainEditor({
  draft,
  keptCount,
  traders,
  isPending,
  onChange,
  onSubmit,
  onCancel
}: ProcessAgainEditorProps) {
  const firstRef = useRef<HTMLButtonElement>(null)
  const traderName = draft.traderName ?? 'The trader'
  const canSubmit = draft.traderId !== null && !isPending

  useEffect(() => {
    firstRef.current?.focus()
  }, [])

  const traderOptions: ComboboxOption[] = traders.map((trader) => ({
    value: toString(trader.id),
    label: trader.name ?? `Trader ${trader.id}`
  }))

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault()
      onCancel()
      return
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      if (canSubmit) onSubmit()
    }
  }

  return (
    <div className="mt-2 flex flex-col gap-3" onKeyDown={onKeyDown}>
      <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-sm">
        <span className="text-muted-foreground text-xs">Trader</span>
        <Combobox
          ref={firstRef}
          options={traderOptions}
          value={draft.traderId === null ? '' : toString(draft.traderId)}
          onChange={(next) => {
            if (next === '') return
            const id = toInteger(next)
            const name = traders.find((trader) => trader.id === id)?.name ?? `Trader ${id}`
            onChange(withTrader(draft, id, name))
          }}
          triggerLabel="Choose the trader…"
          placeholder="Search traders…"
          disabled={isPending}
          size="sm"
          className="h-8 w-full max-w-80 justify-between"
        />

        <span className="text-muted-foreground text-xs">Direction</span>
        <div>
          <ChoiceGroup<PremiseDraft['side']>
            label="Which side of the deal the trader is on"
            choices={[
              { value: null, label: 'Unset' },
              ...TRADER_SIDES.map((side) => ({ value: side, label: `${traderName} ${side}` }))
            ]}
            value={draft.side}
            disabled={isPending}
            onChange={(side) => onChange({ ...draft, side })}
          />
        </div>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-muted-foreground text-xs">Also tell the run</span>
        <Textarea
          value={draft.note}
          onChange={(event) => onChange({ ...draft, note: event.target.value })}
          maxLength={REVIEWER_HINT_LIMITS.note}
          rows={2}
          placeholder="The second page is the supplier’s quote; ignore it."
          disabled={isPending}
        />
      </label>

      {keptCount > 0 && (
        <p className="text-muted-foreground text-xs">
          Keeps {keptCount} confirmed {keptCount === 1 ? 'value' : 'values'}.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!canSubmit} onClick={onSubmit}>
          {isPending ? 'Queueing…' : 'Process again'}
        </Button>
        <Button variant="outline" size="sm" disabled={isPending} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
